import React, { Children, ReactNode } from 'react'
import SplitterLayout from 'react-splitter-layout'
import { useDebouncedCallback } from 'use-debounce'

import { twMerge } from '../../tailwind/merge'
import { useMeasuredWidth } from '../../useMeasuredWidth'
import { HStack } from '../hstack'
import type { ResizableSplitPanelProps } from './types'

const splitterClasses = [
    'relative flex h-full w-full flex-1 flex-row overflow-hidden bg-transparent',
    '[&_.layout-pane]:relative [&_.layout-pane]:h-full [&_.layout-pane]:flex-none [&_.layout-pane]:overflow-hidden',
    '[&_.layout-pane.layout-pane-primary]:flex-auto [&_.layout-pane:last-child]:-ml-px',
    '[&_.layout-pane-primary>*]:relative [&_.layout-pane-primary>*]:h-full [&_.layout-pane-primary>*]:overflow-hidden',
    '[&_.layout-splitter]:z-[2] [&_.layout-splitter]:h-full [&_.layout-splitter]:w-0.5 [&_.layout-splitter]:flex-none [&_.layout-splitter]:cursor-col-resize',
    '[&_.layout-splitter]:border-l [&_.layout-splitter]:[border-left-style:solid] [&_.layout-splitter]:border-border-subtle [&_.layout-splitter]:bg-transparent',
    '[&_.layout-splitter:hover]:border-border-strong',
    '[&.layout-changing]:cursor-col-resize [&.layout-changing_.layout-splitter]:bg-graphics-grey',
].join(' ')

const RESIZE_DEBOUNCE_MS = 100

export const ResizableSplitPanel = ({
    initialRightSizes,
    leftMinSizes,
    rightMinSizes,
    leftMinPxSizes,
    onResize,
    className,
    children,
}: ResizableSplitPanelProps) => {
    const measured = useMeasuredWidth()
    const width = measured.width

    const primaryMinSize = (index: number) => {
        const minPx = leftMinPxSizes?.[index]
        if (!minPx || width <= 0) {
            return leftMinSizes[index]
        }
        return Math.max(leftMinSizes[index], (minPx / width) * 100)
    }
    // `SplitterLayout` reads `secondaryInitialSize` once, on mount, and its own
    // clamp runs only while dragging — a persisted size that predates the floor
    // would come back unclamped, so the columns wait for the measurement.
    const secondaryInitialSize = (index: number) => {
        const minSize = primaryMinSize(index)
        return minSize
            ? Math.min(initialRightSizes[index], 100 - minSize)
            : initialRightSizes[index]
    }
    const awaitingMeasure = leftMinPxSizes !== undefined && width <= 0

    const resizeColumn = useDebouncedCallback((value: number, index: number) => {
        const sizes = [...initialRightSizes]
        sizes[index] = value
        onResize?.(sizes)
    }, RESIZE_DEBOUNCE_MS)

    const [first, ...rest] = Children.toArray(children)
    const columns = rest.reduce<ReactNode>(
        (left, right, index) => (
            <SplitterLayout
                key={index}
                customClassName={splitterClasses}
                percentage
                primaryMinSize={primaryMinSize(index)}
                secondaryMinSize={rightMinSizes[index]}
                secondaryInitialSize={secondaryInitialSize(index)}
                onSecondaryPaneSizeChange={value => resizeColumn(value, index)}
            >
                {[left, right]}
            </SplitterLayout>
        ),
        first,
    )

    return (
        <HStack {...measured.props} className={twMerge('h-full w-full shrink', className)}>
            {!awaitingMeasure && columns}
        </HStack>
    )
}

ResizableSplitPanel.displayName = 'ResizableSplitPanel'
